import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Pencil, Image, Trash2 } from "lucide-react";
import {
  useDeleteProductMutation,
  useGetAdminProductsQuery,
} from "../../redux/api/productsApi";

import Loader from "../layout/Loader";
import AdminProfile from "../user/AdminProfile";
import { Helmet } from "react-helmet-async";

const ListProducts = () => {
  const { data, isLoading, error } = useGetAdminProductsQuery();

  const [
    deleteProduct,
    { isLoading: isDeleteLoading, error: deleteError, isSuccess },
  ] = useDeleteProductMutation();

  useEffect(() => {
    if (error) toast.error(error?.data?.message);
    if (deleteError) toast.error(deleteError?.data?.message);
    if (isSuccess) toast.success("Product deleted successfully");
  }, [error, deleteError, isSuccess]);

  const deleteProductHandler = (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      deleteProduct(id);
    }
  };

  // if (isLoading) return <Loader />;

  return (
    <AdminProfile>
      <Helmet>
        <title>Products</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-6xl mx-auto mt-10 ">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-playfair text-[#1c1917]">
            Admin Dashbord - Products
          </h1>

          <Link
            to="/admin/product/new"
            className="bg-[#BC3F4A] hover:bg-[#a3323b] text-white font-playfair text-sm px-4 py-2 rounded-md shadow-sm transition"
          >
            + Add Product
          </Link>
        </div>

        {/* Table (Grid Layout for Responsiveness) */}
        <div className="bg-[#f5f3ea] rounded-xl border border-[#e2e1d8] shadow-sm overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-4 md:grid-cols-6 bg-white/80 border-b border-[#e2e1d8] text-[#1c1917] text-xs sm:text-sm font-playfair uppercase tracking-wide py-3 px-4">
            <div className="col-span-2">Name</div>
            <div>Stock</div>
            <div className="hidden md:block">Price</div>
            <div className="hidden md:block">Category</div>
            <div className="text-right">Actions</div>
          </div>

          {/* Product Rows */}
          <div className="divide-y divide-[#e2e1d8] font-sourcesans text-sm">
            {data?.products?.length > 0 ? (
              data.products.map((product) => (
                <div
                  key={product._id}
                  className="grid grid-cols-4 md:grid-cols-6 items-center py-4 px-4 hover:bg-white/60 transition"
                >
                  <div className="col-span-2 font-medium text-[#1c1917] truncate">
                    {product.name}
                  </div>

                  {/* Stock indicator */}
                  <div>
                    {product.stock > 0 ? (
                      <span className="px-3 py-1 text-xs rounded-full bg-green-100 text-green-700">
                        {product.stock}
                      </span>
                    ) : (
                      <span className="px-3 py-1 text-xs rounded-full bg-[#BC3F4A]/10 text-[#BC3F4A]">
                        Out of Stock
                      </span>
                    )}
                  </div>

                  <div className="hidden md:block text-stone-700">
                    ${product.price?.toFixed(2)}
                  </div>
                  <div className="hidden md:block text-stone-600">
                    {product.category || "—"}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <Link
                      to={`/admin/products/${product._id}`}
                      className="p-1.5 border border-[#8a4b32]/50 rounded-md text-xs text-[#1c1917] hover:border-[#BC3F4A] hover:text-[#BC3F4A] transition"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Link>

                    <Link
                      to={`/admin/products/${product._id}/upload_images`}
                      className="p-1.5 border border-green-600/40 rounded-md text-xs text-green-700 hover:border-green-700 hover:text-green-700 transition"
                      title="Upload Images"
                    >
                       <Image className="w-3.5 h-3.5" />
                    </Link>

                    <button
                      onClick={() => deleteProductHandler(product._id)}
                      disabled={isDeleteLoading}
                      className="p-1.5 border border-[#BC3F4A]/60 rounded-md text-xs text-[#BC3F4A] hover:bg-[#BC3F4A] hover:text-white transition disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-stone-600">
                No products found.
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminProfile>
  );
};

export default ListProducts;
